import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { Point } from '../../../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../../../common/maths/geometry/rect/rectTypes';
import { ZOOM_PROPS } from '../../../../canvas/zoom/zoomConstants';
import { clampPanToBounds } from '../utils/imageModalPanBoundsUtils';
import {
    getRestZoomOutPan,
    getZoomTranslationForScale,
    isViewportCentreAnchor,
    isZeroTranslation,
} from '../utils/imageModalZoomGeometryUtils';
import { ImageModalZoomState } from '../utils/imageModalZoomTypes';
import { CommitImageModalZoomState } from './useImageModalZoomStore';

// Snap to the target once we're within this relative distance, so we don't animate forever.
const ZOOM_SCALE_SNAP_THRESHOLD = 0.002;

export type ImageModalZoomAnimationTarget = {
    scale: number;
    anchorPoint: Point;
    startScale: number;
    startTranslation: Point;
    recentreToViewportCentre: boolean;
};

type UseImageModalZoomAnimationResult = {
    animationTargetRef: MutableRefObject<ImageModalZoomAnimationTarget | null>;
    animateZoomToScale: (targetScale: number, anchorPoint: Point) => void;
    isZoomAnimating: boolean;
    stopZoomAnimation: () => void;
};

/**
 * Runs smooth zoom animations without forcing React to render every animation frame.
 */
export const useImageModalZoomAnimation = ({
    clearPanEdgeSession,
    commitPanEdgeSession,
    commitZoomState,
    contentSizeRef,
    viewportSizeRef,
    zoomStateRef,
}: {
    clearPanEdgeSession: () => void;
    commitPanEdgeSession: (translation: Point, scale: number) => void;
    commitZoomState: CommitImageModalZoomState;
    contentSizeRef: MutableRefObject<RectSize>;
    viewportSizeRef: MutableRefObject<RectSize>;
    zoomStateRef: MutableRefObject<ImageModalZoomState>;
}): UseImageModalZoomAnimationResult => {
    const [isZoomAnimating, setIsZoomAnimating] = useState(false);
    const animationFrameRef = useRef<number | null>(null);
    // Holds the scale we're easing towards, the viewport anchor, and zoom-out recentring start state.
    const animationTargetRef = useRef<ImageModalZoomAnimationTarget | null>(null);

    /**
     * Cancels the active animation and commits the latest imperative zoom state back to React.
     */
    const stopZoomAnimation = useCallback(() => {
        if (animationFrameRef.current !== null) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (!animationTargetRef.current) return;

        animationTargetRef.current = null;
        setIsZoomAnimating(false);
        commitZoomState(zoomStateRef.current);
    }, [commitZoomState, zoomStateRef]);

    /**
     * Advances one keyboard/button zoom frame, mirroring the canvas's rAF-driven zoom.
     */
    const runZoomAnimationFrame = useCallback(() => {
        const target = animationTargetRef.current;
        if (!target) {
            animationFrameRef.current = null;
            return;
        }

        const current = zoomStateRef.current;
        const easedScale = current.scale + (target.scale - current.scale) * ZOOM_PROPS.INTERPOLATION_FACTOR;
        const isAtTarget = Math.abs(target.scale - easedScale) / target.scale < ZOOM_SCALE_SNAP_THRESHOLD;
        const nextScale = isAtTarget ? target.scale : easedScale;

        const nextTranslation = target.recentreToViewportCentre
            ? getRestZoomOutPan({
                  startScale: target.startScale,
                  startPan: target.startTranslation,
                  nextScale,
              })
            : getZoomTranslationForScale({
                  anchorPoint: target.anchorPoint,
                  currentScale: current.scale,
                  currentTranslation: current.translation,
                  nextScale,
                  viewportSize: viewportSizeRef.current,
              });

        const isZoomIn = target.scale > target.startScale;
        const translation =
            target.recentreToViewportCentre || isZoomIn
                ? nextTranslation
                : clampPanToBounds(nextTranslation, {
                      scale: nextScale,
                      viewportSize: viewportSizeRef.current,
                      contentSize: contentSizeRef.current,
                  });

        const nextZoomState = { scale: nextScale, translation };
        const hasReachedTarget = nextScale === target.scale;

        commitZoomState(nextZoomState, { syncReact: hasReachedTarget });

        if (hasReachedTarget) {
            commitPanEdgeSession(translation, nextScale);
            animationFrameRef.current = null;
            animationTargetRef.current = null;
            setIsZoomAnimating(false);
            return;
        }

        animationFrameRef.current = requestAnimationFrame(runZoomAnimationFrame);
    }, [commitPanEdgeSession, commitZoomState, contentSizeRef, viewportSizeRef, zoomStateRef]);

    /**
     * Starts or retargets the smooth zoom animation for a viewport anchor point.
     */
    const animateZoomToScale = useCallback(
        (targetScale: number, anchorPoint: Point) => {
            const isNewAnimation = animationFrameRef.current === null;
            const current = zoomStateRef.current;
            const recentreToViewportCentre =
                isViewportCentreAnchor(anchorPoint, viewportSizeRef.current) &&
                !isZeroTranslation(current.translation) &&
                targetScale < current.scale;

            // The animation target must be set before any React state update below. Setting state can
            // synchronously flush a pending clamp effect (scheduled when the previous animation
            // completed); the target ref is what stops that stale effect re-clamping the pan against
            // a pan edge session this animation is about to replace.
            animationTargetRef.current = {
                scale: targetScale,
                anchorPoint,
                startScale: current.scale,
                startTranslation: { ...current.translation },
                recentreToViewportCentre,
            };

            if (isNewAnimation) {
                clearPanEdgeSession();
                setIsZoomAnimating(true);
                animationFrameRef.current = requestAnimationFrame(runZoomAnimationFrame);
            }
        },
        [clearPanEdgeSession, runZoomAnimationFrame, viewportSizeRef, zoomStateRef],
    );

    useEffect(() => stopZoomAnimation, [stopZoomAnimation]);

    return {
        animationTargetRef,
        animateZoomToScale,
        isZoomAnimating,
        stopZoomAnimation,
    };
};
